/**
 * Entitlement checks — the one place that decides whether a user can access a
 * given feature. Every gate in the UI should call through here so the
 * "billing off" escape hatch and the Pro-status logic live in a single spot.
 *
 * Key rule: while `BILLING_ENABLED` is false, NOTHING is gated — every check
 * returns full access so the app behaves exactly as it does today. Gating only
 * takes effect once billing is switched on.
 */

import type { UserProfile } from '../auth/types';
import {
  BILLING_ENABLED,
  FREE_MODES,
  PRO_MODES,
  type GameMode,
  type SubscriptionStatus,
} from '../config/plans';

/** Subscription statuses that grant Pro access. `past_due` keeps access during Stripe's retry/dunning window. */
const ACTIVE_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];

/**
 * True if the profile has an entitlement that grants Pro features, judged purely
 * on subscription data (ignores the billing flag). Use `isPro` for gating.
 */
export function hasProEntitlement(profile: UserProfile | null | undefined): boolean {
  if (!profile) return false;
  if (profile.plan === 'pro') return true;
  // Fall back to status in case plan wasn't denormalized for some reason.
  return ACTIVE_STATUSES.includes(profile.subscriptionStatus ?? null);
}

/**
 * Whether the user should be treated as Pro for feature-access decisions.
 *
 * - Billing OFF  → everyone is effectively Pro (no gating, current behavior).
 * - Billing ON   → only users with an active entitlement are Pro.
 *
 * Devs and admins always get Pro (they manage/test the app and, for admins,
 * resell access to invited users).
 */
export function isPro(profile: UserProfile | null | undefined): boolean {
  if (!BILLING_ENABLED) return true;
  if (!profile) return false;
  if (profile.roles?.includes('dev') || profile.roles?.includes('admin')) return true;
  // B2B: a seat inherits Pro while its inviting admin's team plan is active.
  if (profile.orgPlanActive) return true;
  return hasProEntitlement(profile);
}

/** Whether a given game mode requires Pro. */
export function isProMode(mode: GameMode): boolean {
  return PRO_MODES.includes(mode);
}

/** Whether a given game mode is part of the free tier. */
export function isFreeMode(mode: GameMode): boolean {
  return FREE_MODES.includes(mode);
}

/**
 * Whether the user can access a given mode right now. Free modes are always
 * accessible; Pro modes require `isPro`. With billing off, everything is open.
 */
export function canAccessMode(mode: GameMode, profile: UserProfile | null | undefined): boolean {
  if (!isProMode(mode)) return true;
  return isPro(profile);
}

/**
 * Whether a mode should render a "Pro" lock badge in the nav: only when it is a
 * Pro mode, billing is live, and this user hasn't unlocked it.
 */
export function shouldShowProBadge(mode: GameMode, profile: UserProfile | null | undefined): boolean {
  return BILLING_ENABLED && isProMode(mode) && !isPro(profile);
}
